import React, { useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { listCertifications, listLearningProgress } from '../../resources/services/learningCenterService';
import type { Certification } from '../../resources/types';
import type { Partner, PartnerLocation } from '../../partners/types';
import type { Squad, User } from '../../../types';
import { downloadCsv, flattenForCsv, type CsvRow } from '../utils/csvExport';
import '../styles/AdminExportsTab.css';

type RawRecord = Record<string, any> & { id: string };

const LEGACY_EXPORT_FIELDS = [
  'legacySchoolIds', 'legacySchoolId', 'legacyContactIds', 'legacyFieldPath',
  'legacyStoragePath', 'legacyOpsFlowIds', 'schoolId', 'schoolSlug',
];

const OPERATION_BLOCKED_FIELDS = new Set([
  ...LEGACY_EXPORT_FIELDS,
  'studentIds', 'onlineStudentIds', 'enrolledKidNames', 'enrolledParentNamesAndEmails',
  'attendance', 'attendanceMetadata', 'missionProgress',
]);

const TEACHER_BLOCKED_FIELDS = new Set(LEGACY_EXPORT_FIELDS);

const dateStamp = () => new Date().toISOString().slice(0, 10);
const recordFromDoc = (item: any): RawRecord => ({ id: item.id, ...item.data() });
const asDate = (value: any): Date | null => {
  const raw = typeof value?.toDate === 'function' ? value.toDate() : value;
  const date = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const studentCount = (squad: Squad) => new Set([
  ...(Array.isArray(squad.studentIds) ? squad.studentIds : []),
  ...(Array.isArray(squad.onlineStudentIds) ? squad.onlineStudentIds : []),
]).size;

const safeFilenamePart = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'all-seasons';

const AdminExportsTab: React.FC = () => {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [seasonId, setSeasonId] = useState('');
  const [seasonOptions, setSeasonOptions] = useState<Array<{ id: string; label: string }>>([]);

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key); setError('');
    try { await action(); }
    catch (nextError) {
      console.error(`[AdminExportsTab] ${key} export failed`, nextError);
      setError(nextError instanceof Error ? nextError.message : 'Export failed.');
    } finally { setBusy(null); }
  };

  const buildOperationsRows = async (): Promise<CsvRow[]> => {
    const [partnerSnap, locationSnap, squadSnap] = await Promise.all([
      getDocs(collection(db, 'partners')),
      getDocs(collection(db, 'locations')),
      getDocs(collection(db, 'squads')),
    ]);
    const partners = partnerSnap.docs.map(recordFromDoc) as Partner[];
    const locations = locationSnap.docs.map(recordFromDoc) as PartnerLocation[];
    const squads = squadSnap.docs.map(recordFromDoc) as Squad[];
    const rows: CsvRow[] = [];

    partners.forEach(partner => {
      const partnerLocations = locations.filter(location => location.partnerId === partner.id);
      const locationsOrPlaceholder: Array<PartnerLocation | null> = partnerLocations.length ? partnerLocations : [null];
      locationsOrPlaceholder.forEach(location => {
        const relatedSquads = squads.filter(squad => squad.partnerId === partner.id && (!location || squad.locationId === location.id));
        const squadsOrPlaceholder: Array<Squad | null> = relatedSquads.length ? relatedSquads : [null];
        squadsOrPlaceholder.forEach(squad => rows.push({
          ...flattenForCsv(partner as unknown as RawRecord, 'partner', OPERATION_BLOCKED_FIELDS),
          ...flattenForCsv(location as unknown as RawRecord, 'location', OPERATION_BLOCKED_FIELDS),
          ...flattenForCsv(squad as unknown as RawRecord, 'squad', OPERATION_BLOCKED_FIELDS),
          'squad.studentCount': squad ? studentCount(squad) : '',
        }));
      });
      if (partnerLocations.length) {
        const locationIds = new Set(partnerLocations.map(location => location.id));
        squads.filter(squad => squad.partnerId === partner.id && !locationIds.has(String(squad.locationId || ''))).forEach(squad => rows.push({
          ...flattenForCsv(partner as unknown as RawRecord, 'partner', OPERATION_BLOCKED_FIELDS),
          ...flattenForCsv(squad as unknown as RawRecord, 'squad', OPERATION_BLOCKED_FIELDS),
          'squad.studentCount': studentCount(squad),
        }));
      }
    });

    // Preserve canonical records that have not yet been linked during migration.
    locations.filter(location => !partners.some(partner => partner.id === location.partnerId)).forEach(location => {
      const related = squads.filter(squad => squad.locationId === location.id);
      (related.length ? related : [null]).forEach(squad => rows.push({
        ...flattenForCsv(location as unknown as RawRecord, 'location', OPERATION_BLOCKED_FIELDS),
        ...flattenForCsv(squad as unknown as RawRecord, 'squad', OPERATION_BLOCKED_FIELDS),
        'squad.studentCount': squad ? studentCount(squad) : '',
      }));
    });
    squads.filter(squad => !partners.some(partner => partner.id === squad.partnerId) && !locations.some(location => location.id === squad.locationId)).forEach(squad => rows.push({
      ...flattenForCsv(squad as unknown as RawRecord, 'squad', OPERATION_BLOCKED_FIELDS),
      'squad.studentCount': studentCount(squad),
    }));
    return rows;
  };

  const exportOperations = () => run('operations', async () => {
    downloadCsv(`overture-partners-locations-squads-${dateStamp()}.csv`, await buildOperationsRows());
  });

  const buildTeacherRows = async (): Promise<CsvRow[]> => {
    const [usersSnap, teacherSnap, certifications, progress] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'teachers')),
      listCertifications(true),
      listLearningProgress(),
    ]);
    const complianceById = new Map(teacherSnap.docs.map(item => [item.id, item.data()]));
    const teachers = usersSnap.docs.map(recordFromDoc).filter(user => user.role === 'teacher') as User[];
    const rows = teachers.map(teacher => {
      const row: CsvRow = flattenForCsv({ ...teacher, ...(complianceById.get(teacher.id) || {}) }, 'teacher', TEACHER_BLOCKED_FIELDS);
      const teacherProgress = progress.filter(entry => entry.teacherId === teacher.id);
      certifications.forEach((certification: Certification) => {
        const entries = teacherProgress.filter(entry => entry.certificationId === certification.id);
        const completed = certification.steps.filter(step => entries.some(entry => entry.stepId === step.id && entry.status === 'completed')).length;
        const base = `certification.${certification.title}`;
        row[`${base}.completedSteps`] = completed;
        row[`${base}.totalSteps`] = certification.steps.length;
        row[`${base}.percentComplete`] = certification.steps.length ? Math.round(completed / certification.steps.length * 100) : 0;
        row[`${base}.status`] = certification.steps.length > 0 && completed === certification.steps.length ? 'completed' : completed > 0 ? 'in_progress' : 'not_started';
        certification.steps.forEach(step => {
          const entry = entries.find(candidate => candidate.stepId === step.id);
          row[`${base}.step.${step.title}.status`] = entry?.status || 'not_started';
          row[`${base}.step.${step.title}.evidenceUrl`] = entry?.evidence?.url || '';
        });
      });
      return row;
    });
    return rows;
  };

  const exportTeachers = () => run('teachers', async () => {
    downloadCsv(`overture-teachers-${dateStamp()}.csv`, await buildTeacherRows());
  });

  const loadSeasons = async () => {
    const [seasonSnap, squadSnap] = await Promise.all([
      getDocs(collection(db, 'organizationSeasons')),
      getDocs(collection(db, 'squads')),
    ]);
    const labels = new Map<string, string>();
    seasonSnap.docs.forEach(item => labels.set(item.id, String(item.data().label || item.data().termId || item.id)));
    squadSnap.docs.forEach(item => {
      const data = item.data();
      const id = String(data.seasonId || data.termId || '').trim();
      if (id && !labels.has(id)) labels.set(id, String(data.term || id));
    });
    const options = Array.from(labels, ([id, label]) => ({ id, label })).sort((a, b) => b.id.localeCompare(a.id));
    setSeasonOptions(options);
    if (!seasonId && options[0]) setSeasonId(options[0].id);
  };

  const buildCalendarRows = async (calendarSeasonId: string): Promise<CsvRow[]> => {
    const [eventSnap, squadSnap, partnerSnap, locationSnap, userSnap] = await Promise.all([
      getDocs(collection(db, 'squadEvents')),
      getDocs(collection(db, 'squads')),
      getDocs(collection(db, 'partners')),
      getDocs(collection(db, 'locations')),
      getDocs(collection(db, 'users')),
    ]);
    const squads = squadSnap.docs.map(recordFromDoc) as Squad[];
    const squadById = new Map(squads.map(squad => [squad.id, squad]));
    const partnerById = new Map(partnerSnap.docs.map(item => [item.id, recordFromDoc(item)]));
    const locationById = new Map(locationSnap.docs.map(item => [item.id, recordFromDoc(item)]));
    const teacherById = new Map<string, RawRecord>(userSnap.docs
      .map(item => [item.id, recordFromDoc(item)] as const)
      .filter(([, user]) => user.role === 'teacher'));
    const selectedSquadIds = new Set(squads.filter(squad => !calendarSeasonId || String(squad.seasonId || squad.termId || '') === calendarSeasonId).map(squad => squad.id));
    const rows = eventSnap.docs.map(recordFromDoc).filter(event => selectedSquadIds.has(String(event.squadId || ''))).map(event => {
      const squad = squadById.get(String(event.squadId || ''));
      const partner = partnerById.get(String(squad?.partnerId || ''));
      const location = locationById.get(String(squad?.locationId || ''));
      const mainTeacher = teacherById.get(String(event.mainTeacherId || ''));
      const supportingIds = Array.isArray(event.supportingTeacherIds) ? event.supportingTeacherIds.map(String) : [];
      const supportingNames = supportingIds.map(id => teacherById.get(id)?.displayName || teacherById.get(id)?.email || id);
      const start = asDate(event.start); const end = asDate(event.end);
      return {
        eventId: event.id,
        seasonId: calendarSeasonId || String(squad?.seasonId || squad?.termId || ''),
        date: start?.toISOString().slice(0, 10) || '',
        start: start?.toISOString() || '', end: end?.toISOString() || '',
        timezone: String(squad?.timezone || partner?.timezone || ''),
        title: String(event.title || squad?.title || ''), squadId: String(event.squadId || ''),
        squadStatus: String(squad?.status || ''), partnerId: String(squad?.partnerId || ''), partnerName: String(partner?.name || ''),
        locationId: String(squad?.locationId || ''), locationName: String(location?.name || event.locationName || ''),
        address: String(location?.address || event.address || ''),
        mainTeacherId: String(event.mainTeacherId || ''), mainTeacherName: String(mainTeacher?.displayName || mainTeacher?.email || ''),
        supportingTeacherIds: supportingIds.join(' | '), supportingTeacherNames: supportingNames.join(' | '),
        subStatus: String(event.subStatus || 'none'), syncedFromSquadDetails: Boolean(event.syncedFromSquadDetails),
      } as CsvRow;
    }).sort((a, b) => String(a.start).localeCompare(String(b.start)));
    return rows;
  };

  const exportCalendar = () => run('calendar', async () => {
    downloadCsv(`overture-calendar-${safeFilenamePart(seasonId)}-${dateStamp()}.csv`, await buildCalendarRows(seasonId));
  });

  const exportCombined = () => run('combined', async () => {
    const [operationRows, teacherRows, calendarRows] = await Promise.all([
      buildOperationsRows(),
      buildTeacherRows(),
      buildCalendarRows(''),
    ]);
    downloadCsv(`overture-all-data-${dateStamp()}.csv`, [
      ...operationRows.map(row => ({ recordType: 'operation', ...row })),
      ...teacherRows.map(row => ({ recordType: 'teacher', ...row })),
      ...calendarRows.map(row => ({ recordType: 'calendar_event', ...row })),
    ]);
  });

  const selectedSeasonLabel = useMemo(() => seasonOptions.find(item => item.id === seasonId)?.label || seasonId, [seasonId, seasonOptions]);

  return <div className="admin-exports">
    <header><span>Data portability</span><h2>Data exports</h2><p>Download canonical operational data and secure files for approved external workflows.</p></header>
    {error && <div className="admin-exports__error" role="alert">{error}</div>}
    <div className="admin-exports__grid">
      <article><div className="admin-exports__icon">🏢</div><h3>Partners, locations &amp; squads</h3><p>One relational CSV with every canonical field. Student and parent identities are excluded; each squad includes a student count.</p><button disabled={Boolean(busy)} onClick={exportOperations}>{busy === 'operations' ? 'Preparing…' : 'Export operations CSV'}</button></article>
      <article><div className="admin-exports__icon">👩‍🏫</div><h3>Teachers &amp; certifications</h3><p>Teacher profiles, employment and compliance fields, plus overall and step-level certification progress.</p><button disabled={Boolean(busy)} onClick={exportTeachers}>{busy === 'teachers' ? 'Preparing…' : 'Export teachers CSV'}</button></article>
      <article><div className="admin-exports__icon">🗓️</div><h3>Season calendar events</h3><p>Every scheduled event for squads in the selected season, including partner, location, and teacher assignments.</p>
        <label>Season<select value={seasonId} onFocus={() => { if (!seasonOptions.length) void loadSeasons(); }} onChange={event => setSeasonId(event.target.value)}><option value="">All seasons</option>{seasonOptions.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
        {!seasonOptions.length && <button className="admin-exports__secondary" disabled={Boolean(busy)} onClick={() => run('seasons', loadSeasons)}>{busy === 'seasons' ? 'Loading…' : 'Load seasons'}</button>}
        <button disabled={Boolean(busy)} onClick={exportCalendar}>{busy === 'calendar' ? 'Preparing…' : `Export ${selectedSeasonLabel || 'all'} events`}</button>
      </article>
      <article><div className="admin-exports__icon">📦</div><h3>All data combined</h3><p>Operations, teachers and certification progress, plus calendar events from every season in one CSV. Use the recordType column to filter each dataset.</p><button disabled={Boolean(busy)} onClick={exportCombined}>{busy === 'combined' ? 'Preparing everything…' : 'Export all data CSV'}</button></article>
    </div>
    <aside><strong>Privacy guardrail:</strong> exports omit legacy identifiers. The operations export also excludes student IDs, student names, parent names or emails, attendance records, and student-level progress.</aside>
  </div>;
};

export default AdminExportsTab;
